import {Rule} from '../../models/svg-rule/rule.model';
import {IRuleSystem} from '../../models/svg-rule/rule-system.model';
import {Color} from '../../enums/color';
import {TcddTrainTabUtil, ValueIndex} from '../../utils/tcdd-train-tab-util';
import {TcddMainSystem} from '../../enums/tcdd-main-system.enum';
import {TcddSubSystem} from '../../enums/tcdd-sub-system.enum';
import {TcddSubSystemType} from '../../enums/tcdd-sub-system-type.enum';
import {TcddWagonTitle} from '../../enums/index-map/tcdd-wagon-title.enum';
import {MainSystemKeys} from '../../constants/tcdd-system-keys/main-system-keys';
import {OtherSystemKeys} from '../../constants/tcdd-system-keys/other-system-keys';
import {OTHER_SYSTEM} from '../../constants/tcdd-system-contants/other-system.constant';
import {TCDD_UNIT} from '../../constants/tcdd-unit-constant';
import {createColorRule, createIconRule} from '../../utils/tcdd-rule-util';

enum LightSide {
  LEFT = 'left',
  RIGHT = 'right',
  UP = 'up'
}

function getIndex(key: string): ValueIndex {
  return TcddTrainTabUtil.getValueIndex(
    TcddMainSystem.DIAGNOSTIC_SYSTEM,
    TcddSubSystem.OTHER_SYSTEM,
    TcddSubSystemType.SUB,
    key
  );
}

export class OtherSystemRules implements IRuleSystem {
  private static readonly LIGHT_ICONS = {
    FULL_MARKER_FULL_HEAD:       'train/unit-full-marker-light-and-full-head-light.svg',
    FULL_MARKER_DIMMED_HEAD:     'train/unit-full-marker-light-and-dimmed-head-light.svg',
    TAIL_AND_FULL_HEAD:          'train/unit-tail-light-and-full-head-light.svg',
    FULL_HEAD:                   'train/unit-full-head-light.svg',
    DIMMED_HEAD:                 'train/unit-dimmed-head-light.svg',
    FULL_MARKER:                 'train/unit-full-marker-light.svg',
    DIMMED_MARKER:               'train/unit-dimmed-marker-light.svg',
    TAIL:                        'train/unit-tail-light.svg',
    OFF_FRONT:                   'train/unit-off-front-light.svg',
    OFF_BACK:                    'train/unit-off-back-light.svg',
    LIGHT_ON:                    'train/unit-lightOn.svg',
    LIGHT_OFF:                   'train/unit-lightOff.svg',
    LIGHT_HALF:                  'train/unit-halfLight.svg',
    EMERGENCY_LIGHT:             'train/unit-emergencyLight.svg'
  } as const;

  public getRules(): Rule[] {
    const evaluateOutdoorLighting = (
      wagonIndex: number,
      data: any[],
      side: LightSide,
      version: TcddWagonTitle
    ): string => {
      const index: number = version === TcddWagonTitle.SKA1 ? 0 : 1;
      // Variables(ConnectionIndicator) that track the direction of the trains or whether the coupler is open (0 = null, 1 = coupler, 2 = direction).
      // (e.g., [2, 0] means the train is moving to the left and there is nothing on its right).
      const couplerData = data?.[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.connectionIndicator]?.[index];
      if (couplerData === 1) {
        return null;
      }

      const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
      if (!wagonData) {
        return version === TcddWagonTitle.SKA2 && side === LightSide.UP
          ? null
          : (version === TcddWagonTitle.SKA1
            ? OtherSystemRules.LIGHT_ICONS.OFF_FRONT
            : OtherSystemRules.LIGHT_ICONS.OFF_BACK);
      }

      const tailLightRedLeft: ValueIndex = getIndex(OtherSystemKeys.tailLightRedLeft);
      const tailLightRedRight: ValueIndex = getIndex(OtherSystemKeys.tailLightRedRight);
      const dimmedMarkerLightLeftRight: ValueIndex = getIndex(OtherSystemKeys.dimmedMarkerLightLeftRight);
      const dimmedHeadLightLeft: ValueIndex = getIndex(OtherSystemKeys.dimmedHeadLightLeft);
      const dimmedHeadLightRight: ValueIndex = getIndex(OtherSystemKeys.dimmedHeadLightRight);
      const fullMarkerLightLeftRight: ValueIndex = getIndex(OtherSystemKeys.fullMarkerLightLeftRight);
      const fullHeadLightLeftRight: ValueIndex = getIndex(OtherSystemKeys.fullHeadLightLeftRight);

      if ( // full marker and full head
        Number(wagonData[fullMarkerLightLeftRight.groupIndex]?.[fullMarkerLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON &&
        Number(wagonData[fullHeadLightLeftRight.groupIndex]?.[fullHeadLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
        return OtherSystemRules.LIGHT_ICONS.FULL_MARKER_FULL_HEAD;
      }

      if ( // full marker and dimmed head
        Number(wagonData[fullMarkerLightLeftRight.groupIndex]?.[fullMarkerLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON &&
        Number(wagonData[dimmedHeadLightLeft.groupIndex]?.[dimmedHeadLightLeft.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
        return OtherSystemRules.LIGHT_ICONS.FULL_MARKER_DIMMED_HEAD;
      }

      // tail and full head
      if (side === LightSide.LEFT) {
        if (Number(wagonData[tailLightRedLeft.groupIndex]?.[tailLightRedLeft.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON &&
          Number(wagonData[fullHeadLightLeftRight.groupIndex]?.[fullHeadLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
          return OtherSystemRules.LIGHT_ICONS.TAIL_AND_FULL_HEAD;
        }
      } else if (side === LightSide.RIGHT) {
        if (Number(wagonData[tailLightRedRight.groupIndex]?.[tailLightRedRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON &&
          Number(wagonData[fullHeadLightLeftRight.groupIndex]?.[fullHeadLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
          return OtherSystemRules.LIGHT_ICONS.TAIL_AND_FULL_HEAD;
        }
      } else if (side === LightSide.UP) {
        if ((Number(wagonData[tailLightRedLeft.groupIndex]?.[tailLightRedLeft.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON ||
            Number(wagonData[tailLightRedRight.groupIndex]?.[tailLightRedRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON)
          && Number(wagonData[fullHeadLightLeftRight.groupIndex]?.[fullHeadLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
          return OtherSystemRules.LIGHT_ICONS.FULL_HEAD;
        }
      }

      if (Number(wagonData[fullHeadLightLeftRight.groupIndex]?.[fullHeadLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) { // full head
        return OtherSystemRules.LIGHT_ICONS.FULL_HEAD;
      }

      if (Number(wagonData[dimmedHeadLightLeft.groupIndex]?.[dimmedHeadLightLeft.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) { // dimmed head
        return OtherSystemRules.LIGHT_ICONS.DIMMED_HEAD;
      }

      if (Number(wagonData[fullMarkerLightLeftRight.groupIndex]?.[fullMarkerLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) { // full marker
        return OtherSystemRules.LIGHT_ICONS.FULL_MARKER;
      }

      if (Number(wagonData[dimmedMarkerLightLeftRight.groupIndex]?.[dimmedMarkerLightLeftRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) { // dimmed marker
        return OtherSystemRules.LIGHT_ICONS.DIMMED_MARKER;
      }

      // tail
      if (side === LightSide.LEFT) {
        if (Number(wagonData[tailLightRedLeft.groupIndex]?.[tailLightRedLeft.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
          return OtherSystemRules.LIGHT_ICONS.TAIL;
        }
      } else if (side === LightSide.RIGHT) {
        if (Number(wagonData[tailLightRedRight.groupIndex]?.[tailLightRedRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
          return OtherSystemRules.LIGHT_ICONS.TAIL;
        }
      } else if (side === LightSide.UP) {
        if (Number(wagonData[tailLightRedLeft.groupIndex]?.[tailLightRedLeft.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON ||
          Number(wagonData[tailLightRedRight.groupIndex]?.[tailLightRedRight.infoIndex]) === OTHER_SYSTEM.LIGHT_STATE.ON) {
          return null;
        }
      }

      return version === TcddWagonTitle.SKA2 && side === LightSide.UP
        ? null
        : (version === TcddWagonTitle.SKA1
          ? OtherSystemRules.LIGHT_ICONS.OFF_FRONT
          : OtherSystemRules.LIGHT_ICONS.OFF_BACK);
    };

    const evaluateToiletParameters = (
      wagonIndex: number,
      data: any[]
    ): Color => {
      const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
      if (!wagonData) return null;

      const toiletCommunicationError: ValueIndex = getIndex(OtherSystemKeys.toiletCommunicationError); // === 1 will be pink
      const waterLevelLow: ValueIndex = getIndex(OtherSystemKeys.waterLevelLow); // === 1 will be red
      const wasteWaterTankFull: ValueIndex = getIndex(OtherSystemKeys.wasteWaterTankFull); // === 1 will be red
      const toiletBowlFull: ValueIndex = getIndex(OtherSystemKeys.toiletBowlFull); // === 1 will be red
      const toiletSystemAirPressureLow: ValueIndex = getIndex(OtherSystemKeys.toiletSystemAirPressureLow); // === 1 will be red
      const toiletVacuumFailure: ValueIndex = getIndex(OtherSystemKeys.toiletVacuumFailure); // === 1 will be red
      const toiletPressureFailure: ValueIndex = getIndex(OtherSystemKeys.toiletPressureFailure); // === 1 will be red
      const toiletOutOfOrderSignal: ValueIndex = getIndex(OtherSystemKeys.toiletOutOfOrderSignal); // === 1 will be yellow
      const toiletActive: ValueIndex = getIndex(OtherSystemKeys.toiletActive); // === 1 will be blue

      if (Number(wagonData[toiletCommunicationError.groupIndex]?.[toiletCommunicationError.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_COMMUNICATION_ERROR) { // Pink State
        return Color.Pink;
      }

      if ( // Red State
        Number(wagonData[waterLevelLow.groupIndex]?.[waterLevelLow.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_WATER_LEVEL_LOW ||
        Number(wagonData[wasteWaterTankFull.groupIndex]?.[wasteWaterTankFull.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_WASTE_WATER_TANK_FULL ||
        Number(wagonData[toiletBowlFull.groupIndex]?.[toiletBowlFull.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_BOWL_FULL ||
        Number(wagonData[toiletSystemAirPressureLow.groupIndex]?.[toiletSystemAirPressureLow.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_AIR_PRESSURE_LOW ||
        Number(wagonData[toiletVacuumFailure.groupIndex]?.[toiletVacuumFailure.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_VACUUM_FAILURE ||
        Number(wagonData[toiletPressureFailure.groupIndex]?.[toiletPressureFailure.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_PRESSURE_FAILURE) {
        return Color.Red;
      }

      if (Number(wagonData[toiletOutOfOrderSignal.groupIndex]?.[toiletOutOfOrderSignal.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_OUT_OF_ORDER) { // Yellow State
        return Color.Yellow;
      }

      if (Number(wagonData[toiletActive.groupIndex]?.[toiletActive.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_ACTIVE) { // Blue State
        return Color.Blue;
      }

      return null;
    };

    return [
      createIconRule(
        TCDD_UNIT.SKA1_OUTDOOR_LIGHTING_LEFT,
        (systemId: string, wagonIndex: number, data: any[], setColor, setIcon) => {
          const icon = evaluateOutdoorLighting(wagonIndex, data, LightSide.LEFT, TcddWagonTitle.SKA1);
          if (!icon) return false;

          setIcon(icon);
          setColor(Color.Transparent);

          return true;
        },
        {
          iconSize: 16,
          offsetX: 0,
          offsetY: 0,
          position: 'left'
        }
      ),
      createIconRule(
        TCDD_UNIT.SKA1_OUTDOOR_LIGHTING_RIGHT,
        (systemId: string, wagonIndex: number, data: any[], setColor, setIcon) => {
          const icon = evaluateOutdoorLighting(wagonIndex, data, LightSide.RIGHT, TcddWagonTitle.SKA1);
          if (!icon) return false;

          setIcon(icon);
          setColor(Color.Transparent);

          return true;
        },
        {
          iconSize: 16,
          offsetX: 0,
          offsetY: 0,
          position: 'left'
        }
      ),
      createIconRule(
        TCDD_UNIT.SKA1_OUTDOOR_LIGHTING_UP,
        (systemId: string, wagonIndex: number, data: any[], setColor, setIcon) => {
          const icon = evaluateOutdoorLighting(wagonIndex, data, LightSide.UP, TcddWagonTitle.SKA1);
          if (!icon) return false;

          setIcon(icon);
          setColor(Color.Transparent);

          return true;
        },
        {
          iconSize: 16,
          offsetX: 0,
          offsetY: 0,
          position: 'left'
        }
      ),
      createIconRule(
        TCDD_UNIT.SKA2_OUTDOOR_LIGHTING_LEFT,
        (systemId: string, wagonIndex: number, data: any[], setColor, setIcon) => {
          const icon = evaluateOutdoorLighting(wagonIndex, data, LightSide.LEFT, TcddWagonTitle.SKA2);
          if (!icon) return false;

          setIcon(icon);
          setColor(Color.Transparent);

          return true;
        },
        {
          iconSize: 16,
          offsetX: 0,
          offsetY: 0,
          position: 'right'
        }
      ),
      createIconRule(
        TCDD_UNIT.SKA2_OUTDOOR_LIGHTING_RIGHT,
        (systemId: string, wagonIndex: number, data: any[], setColor, setIcon) => {
          const icon = evaluateOutdoorLighting(wagonIndex, data, LightSide.RIGHT, TcddWagonTitle.SKA2);
          if (!icon) return false;

          setIcon(icon);
          setColor(Color.Transparent);

          return true;
        },
        {
          iconSize: 16,
          offsetX: 0,
          offsetY: 0,
          position: 'right'
        }
      ),
      createIconRule(
        TCDD_UNIT.SKA2_OUTDOOR_LIGHTING_UP,
        (systemId: string, wagonIndex: number, data: any[], setColor, setIcon) => {
          const icon = evaluateOutdoorLighting(wagonIndex, data, LightSide.UP, TcddWagonTitle.SKA2);
          if (!icon) return false;

          setIcon(icon);
          setColor(Color.Transparent);

          return true;
        },
        {
          iconSize: 16,
          offsetX: 0,
          offsetY: 0,
          position: 'right'
        }
      ),
      createIconRule(
        TCDD_UNIT.INTERIOR_LIGHTING,
        (systemId: string, wagonIndex: number, data: any[], setColor, setIcon) => {
          const lightOn = data?.[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.lightOn]; // === 1 will be Full Lighting
          const halfLight = data?.[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.halfLight]; // === 1 will be Half Lighting
          const emergencyLight = data?.[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.emergencyLight]; // === 1 will be Emergency Lighting

          if (Number(lightOn) === OTHER_SYSTEM.LIGHT_STATE.ON) {
            setColor(Color.Transparent);
            setIcon(OtherSystemRules.LIGHT_ICONS.LIGHT_ON);
            return true;
          }

          if (Number(halfLight) === OTHER_SYSTEM.LIGHT_STATE.ON) {
            setColor(Color.Transparent);
            setIcon(OtherSystemRules.LIGHT_ICONS.LIGHT_HALF);
            return true;
          }

          if (Number(emergencyLight) === OTHER_SYSTEM.LIGHT_STATE.ON) {
            setColor(Color.Transparent);
            setIcon(OtherSystemRules.LIGHT_ICONS.EMERGENCY_LIGHT);
            return true;
          }

          setColor(Color.Transparent);
          setIcon(OtherSystemRules.LIGHT_ICONS.LIGHT_OFF);
          return true;
        },
        {
          iconSize: 24,
          offsetX: 15,
          offsetY: 0,
          position: 'right'
        }
      ),
      createColorRule(TCDD_UNIT.FIRE_EXTINGUISHER, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const fireExtinguisher1Presence: ValueIndex = getIndex(OtherSystemKeys.fireExtinguisher1Presence); // === 1 will be red
        const fireExtinguisher2Presence: ValueIndex = getIndex(OtherSystemKeys.fireExtinguisher2Presence); // === 1 will be red
        const fireExtinguisher3CabinPresence: ValueIndex = getIndex(OtherSystemKeys.fireExtinguisher3CabinPresence); // === 1 will be red

        if ( // Red state
          Number(wagonData[fireExtinguisher1Presence.groupIndex]?.[fireExtinguisher1Presence.infoIndex]) === OTHER_SYSTEM.PRESENCE.NOT_PRESENT ||
          Number(wagonData[fireExtinguisher2Presence.groupIndex]?.[fireExtinguisher2Presence.infoIndex]) === OTHER_SYSTEM.PRESENCE.NOT_PRESENT ||
          Number(wagonData[fireExtinguisher3CabinPresence.groupIndex]?.[fireExtinguisher3CabinPresence.infoIndex]) === OTHER_SYSTEM.PRESENCE.NOT_PRESENT) {
          setColor(Color.Red);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.PASSENGER_ALARM, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const handle1PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle1PassengerAlarm); // === 1 will be red
        const handle2PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle2PassengerAlarm); // === 1 will be red
        const handle3PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle3PassengerAlarm); // === 1 will be red
        const handle4PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle4PassengerAlarm); // === 1 will be red

        if ( // Normal state(Gray)
          Number(wagonData[handle1PassengerAlarm.groupIndex]?.[handle1PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED &&
          Number(wagonData[handle2PassengerAlarm.groupIndex]?.[handle2PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED &&
          Number(wagonData[handle3PassengerAlarm.groupIndex]?.[handle3PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED &&
          Number(wagonData[handle4PassengerAlarm.groupIndex]?.[handle4PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.PASSENGER_ALARM_1, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const handle1PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle1PassengerAlarm); // === 1 will be red

        if ( // Normal state(Gray)
          Number(wagonData[handle1PassengerAlarm.groupIndex]?.[handle1PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.PASSENGER_ALARM_2, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const handle2PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle2PassengerAlarm); // === 1 will be red

        if ( // Normal state(Gray)
          Number(wagonData[handle2PassengerAlarm.groupIndex]?.[handle2PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.PASSENGER_ALARM_3, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const handle3PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle3PassengerAlarm); // === 1 will be red

        if ( // Normal state(Gray)
          Number(wagonData[handle3PassengerAlarm.groupIndex]?.[handle3PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.PASSENGER_ALARM_4, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const handle4PassengerAlarm: ValueIndex = getIndex(OtherSystemKeys.handle4PassengerAlarm); // === 1 will be red

        if ( // Normal state(Gray)
          Number(wagonData[handle4PassengerAlarm.groupIndex]?.[handle4PassengerAlarm.infoIndex]) === OTHER_SYSTEM.HANDLE_STATUS.NOT_ACTIVATED) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.TOILET_BACKGROUND, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const color = evaluateToiletParameters(wagonIndex, data);
        setColor(color);
        return !!color;
      }),
      createColorRule(TCDD_UNIT.TOILET_TEXT, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const color = evaluateToiletParameters(wagonIndex, data);

        if (color === Color.Yellow) {
          setColor(Color.Black);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.TOILET_CONTAINER, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const toiletCommunicationError: ValueIndex = getIndex(OtherSystemKeys.toiletCommunicationError); // === 1 will be pink
        const wcSosSignal: ValueIndex = getIndex(OtherSystemKeys.wcSosSignal); // === 1 will be SOS

        const hasNotCommunicationError =
          Number(wagonData[toiletCommunicationError.groupIndex]?.[toiletCommunicationError.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_NOT_COMMUNICATION_ERROR;
        const hasSosSignal =
          Number(wagonData[wcSosSignal.groupIndex]?.[wcSosSignal.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_SOS_SIGNAL_ACTIVE;

        if (hasNotCommunicationError && hasSosSignal) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      }),
      createColorRule(TCDD_UNIT.TOILET_SOS_CONTAINER, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.OTHER_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const wcSosSignal: ValueIndex = getIndex(OtherSystemKeys.wcSosSignal); // === 1 will be SOS
        const toiletCommunicationError: ValueIndex = getIndex(OtherSystemKeys.toiletCommunicationError); // === 1 will be pink

        const sosInactive =
          Number(wagonData[wcSosSignal.groupIndex]?.[wcSosSignal.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_SOS_SIGNAL_INACTIVE;
        const communicationError =
          Number(wagonData[toiletCommunicationError.groupIndex]?.[toiletCommunicationError.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_COMMUNICATION_ERROR;

        if (sosInactive || communicationError) {
          setColor(Color.Transparent);
          return true;
        }

        return false;
      })
    ];
  }
}
